import React from "react";
import styled from "styled-components";
import { Row } from "module/common/component/layout";
import { Button } from "module/common/component/input/Button/Button";
import { ChevronArrowLeftIcon, ChevronArrowRightIcon } from "module/common/icons";

export interface PaginationProps {
    page: number;
    count?: number; // Total number of pages (alias for totalPages)
    totalPages?: number;
    onChange?: (page: number) => void; // Alias for onPageChange
    onPageChange?: (page: number) => void;
    startIcon?: boolean; // Show first page button
    endIcon?: boolean; // Show last page button
    boundaryCount?: number; // Number of pages to show at start/end
    siblingCount?: number; // Number of pages to show around current page
}

const PaginationRoot = styled(Row)`
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
`;

const PageButton = styled(Button)<{ active?: boolean }>`
    ${({ active, theme }) =>
        active &&
        `
        background-color: ${theme.palette.primary};
        color: ${theme.palette.white};
    `}
`;

export const Pagination: React.FC<PaginationProps> = ({
    page,
    count,
    totalPages,
    onChange,
    onPageChange,
    startIcon = true,
    endIcon = true,
    boundaryCount = 1,
    siblingCount = 1,
}) => {
    const total = count || totalPages || 1;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const handleChange = onChange || onPageChange || (() => {});

    const generatePageNumbers = () => {
        const pages: (number | string)[] = [];

        // Always show first page if startIcon is true
        if (startIcon && boundaryCount > 0) {
            for (let i = 1; i <= Math.min(boundaryCount, total); i++) {
                pages.push(i);
            }
        }

        // Calculate range around current page
        const startPage = Math.max(boundaryCount + 1, page - siblingCount);
        const endPage = Math.min(total - boundaryCount, page + siblingCount);

        // Add ellipsis if there's a gap
        if (startPage > boundaryCount + 1) {
            pages.push("...");
        }

        // Add pages around current page
        for (let i = startPage; i <= endPage; i++) {
            if (i > boundaryCount && i <= total - boundaryCount) {
                pages.push(i);
            }
        }

        // Add ellipsis if there's a gap
        if (endPage < total - boundaryCount) {
            pages.push("...");
        }

        // Always show last page if endIcon is true
        if (endIcon && boundaryCount > 0) {
            for (let i = Math.max(total - boundaryCount + 1, 1); i <= total; i++) {
                if (i > endPage) {
                    pages.push(i);
                }
            }
        }

        return pages;
    };

    return (
        <PaginationRoot>
            <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => handleChange(page - 1)}>
                <ChevronArrowLeftIcon />
            </Button>
            {generatePageNumbers().map((pageNum, index) =>
                typeof pageNum === "number" ? (
                    <PageButton
                        key={`page-${pageNum}`}
                        variant="outline"
                        size="sm"
                        active={pageNum === page}
                        onClick={() => handleChange(pageNum)}
                    >
                        {pageNum}
                    </PageButton>
                ) : (
                    <span key={`ellipsis-${index}`}>{pageNum}</span>
                )
            )}
            <Button variant="ghost" size="sm" disabled={page === total} onClick={() => handleChange(page + 1)}>
                <ChevronArrowRightIcon />
            </Button>
        </PaginationRoot>
    );
};

export default Pagination;


